import { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';

const AuthContext = createContext(null);

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState(localStorage.getItem('gds_token'));

  useEffect(() => {
    const checkAuth = async () => {
      const storedToken = localStorage.getItem('gds_token');
      if (storedToken) {
        try {
          const response = await axios.get(`${API_URL}/api/auth/me`, {
            headers: { Authorization: `Bearer ${storedToken}` },
            timeout: 10000,
          });
          setUser(response.data);
          setToken(storedToken);
        } catch (error) {
          localStorage.removeItem('gds_token');
          setUser(null);
          setToken(null);
        }
      }
      setLoading(false);
    };

    checkAuth();
  }, []);

  const login = async (email, password) => {
    const response = await axios.post(`${API_URL}/api/auth/login`, { email, password });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('gds_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const register = async (data) => {
    const response = await axios.post(`${API_URL}/api/auth/register`, data);
    const { access_token, user: userData } = response.data;
    localStorage.setItem('gds_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const handleGoogleOAuthCode = async (code, redirectUri) => {
    const response = await axios.post(`${API_URL}/api/auth/google/callback`, {
      code,
      redirect_uri: redirectUri,
    });
    const { access_token, user: userData } = response.data;
    localStorage.setItem('gds_token', access_token);
    setToken(access_token);
    setUser(userData);
    return userData;
  };

  const getGoogleLoginUrl = async () => {
    const redirectUri = window.location.origin + '/auth/callback';
    const response = await axios.get(`${API_URL}/api/auth/google/login-url`, {
      params: { redirect_uri: redirectUri },
    });
    return response.data.auth_url;
  };

  const logout = () => {
    localStorage.removeItem('gds_token');
    setUser(null);
    setToken(null);
  };

  const refreshUser = async () => {
    const storedToken = localStorage.getItem('gds_token');
    if (storedToken) {
      try {
        const response = await axios.get(`${API_URL}/api/auth/me`, {
          headers: { Authorization: `Bearer ${storedToken}` },
          timeout: 10000,
        });
        setUser(response.data);
        return response.data;
      } catch (error) {
        // ignore — stale state is better than logging out
      }
    }
    return null;
  };

  const value = {
    user,
    token,
    loading,
    login,
    register,
    logout,
    refreshUser,
    handleGoogleOAuthCode,
    getGoogleLoginUrl,
    isAuthenticated: !!user,
    isSubscriptionActive: !!user && user.subscription_status !== 'suspended',
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

// Axios instance with timeout, retry, and auth interceptors
export const api = axios.create({
  baseURL: `${API_URL}/api`,
  timeout: 120000,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('gds_token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

const MAX_RETRIES = 2;
// 429 retried only when it's NOT a package-quota error; quota 429s are surfaced as toasts.
const RETRYABLE_STATUS = new Set([408, 502, 503, 504]);

// Endpoints that must NOT trigger the 402/429 redirect loop
const QUOTA_TOAST_EXEMPT = ['/package-usage', '/packages/gates', '/billing'];

let _lastQuotaToastAt = 0;
const QUOTA_TOAST_DEBOUNCE_MS = 4000;

const _redirectToBilling = () => {
  if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/dashboard/billing')) {
    window.location.href = '/dashboard/billing';
  }
};

const _apiDetailMessage = (detail, fallback) => {
  if (detail && typeof detail === 'object') return detail.message || fallback;
  if (typeof detail === 'string') return detail;
  return fallback;
};

const _maybeShowQuotaToast = (status, detail, requestUrl) => {
  // Skip exempt routes — the user is already on or near the billing/usage page
  if (QUOTA_TOAST_EXEMPT.some((p) => requestUrl.includes(p))) return;

  const now = Date.now();
  if (now - _lastQuotaToastAt < QUOTA_TOAST_DEBOUNCE_MS) return;
  _lastQuotaToastAt = now;

  const message = _apiDetailMessage(
    detail,
    status === 402
      ? 'Select a plan to continue.'
      : 'You have reached your package limit. Upgrade your plan to continue.'
  );

  if (status === 402) {
    toast.error(message, {
      description: 'Choose a package to keep working.',
      action: { label: 'Choose plan', onClick: _redirectToBilling },
    });
  } else {
    toast.error(message, {
      description: 'Upgrade your plan or wait for next billing cycle.',
      action: { label: 'Upgrade', onClick: _redirectToBilling },
    });
  }
};

const _normalizeQuotaError = (error, status, detail) => {
  const message = _apiDetailMessage(
    detail,
    status === 402
      ? 'Select a plan to continue.'
      : 'You have reached your package limit. Upgrade your plan to continue.'
  );

  error._gdsUserMessage = message;
  if (error.response?.data) {
    error.response.data.detail = message;
  }
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const config = error.config;
    if (!config) return Promise.reject(error);

    config._retryCount = config._retryCount || 0;
    const status = error.response?.status;
    const requestUrl = config.url || '';
    const detail = error.response?.data?.detail;
    const errorCode = (detail && typeof detail === 'object' && detail.error) || null;

    if (status === 401) {
      const isAuthEndpoint = requestUrl.includes('/auth/me') || requestUrl.includes('/auth/session');
      if (isAuthEndpoint) {
        localStorage.removeItem('gds_token');
        window.location.href = '/login';
      }
      return Promise.reject(error);
    }

    // 402: missing plan or plan doesn't include feature → toast + redirect (debounced, exempt-aware)
    if (status === 402 && (errorCode === 'no_plan' || errorCode === 'plan_limit_zero')) {
      _maybeShowQuotaToast(402, detail, requestUrl);
      _normalizeQuotaError(error, 402, detail);
      return Promise.reject(error);
    }

    // 429 with package-quota detail: toast (do NOT retry — the limit is real)
    if (status === 429 && (errorCode === 'quota_exceeded' || detail?.metric)) {
      _maybeShowQuotaToast(429, detail, requestUrl);
      _normalizeQuotaError(error, 429, detail);
      return Promise.reject(error);
    }

    if (config._retryCount < MAX_RETRIES && (RETRYABLE_STATUS.has(status) || !error.response)) {
      config._retryCount += 1;
      const delay = Math.min(1000 * Math.pow(2, config._retryCount - 1), 5000);
      await new Promise((r) => setTimeout(r, delay));
      return api(config);
    }

    if (!error.response && config._retryCount >= MAX_RETRIES) {
      error._gdsNetworkError = true;
      error._gdsUserMessage =
        "We're having trouble connecting to our servers. Please check your internet connection and try again.";
    }

    return Promise.reject(error);
  }
);
